'use client'

import { AreaItem } from '@/types/cms/AreaItem'
import styles from '@/elements/widgets/Teaser/Slider/TeaserSlider.module.scss'
import { Link } from '@/components/Link'
import { Image } from '@/components/Image'
import React, { useEffect } from 'react'
import { Swiper, SwiperSlide } from 'swiper/react'
import 'swiper/css'
import 'swiper/css/free-mode'
import 'swiper/css/navigation'

import { Keyboard, Navigation } from 'swiper/modules'
import { Icon } from '@/components/Icon'
import SectionTitle from '@/components/SectionTitle/SectionTitle'
import { clsx } from 'clsx'
import TypeVisualizer from '@/elements/widgets/Teaser/components/TypeVisualizer/TypeVisualizer'
import LiveLabel from '@/elements/widgets/Teaser/components/LiveLabel/LiveLabel'

const TeaserSlider = ({ item }: { item: AreaItem }) => {
  const crop = ['Img_16_9', 'Img_3_4'].includes(item.model.fields?.crop as string) ? item.model.fields.crop : 'Img_16_9'

  const [grid, setGrid] = React.useState<string | undefined>(undefined)

  const prevRef = React.useRef(null)
  const nextRef = React.useRef(null)

  const isPortrait = crop === 'Img_3_4'

  useEffect(() => {
    const element = document.documentElement

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0]
      if (entry.contentRect.width <= 743) {
        setGrid('xs')
      } else if (entry.contentRect.width <= 903) {
        setGrid('s')
      } else if (entry.contentRect.width <= 1235) {
        setGrid('m')
      } else if (entry.contentRect.width <= 1375) {
        setGrid('l')
      } else if (entry.contentRect.width <= 1799) {
        setGrid('xl')
      } else {
        setGrid('xxl')
      }
    })

    observer.observe(element)

    return () => observer.disconnect()
  }, [])

  return (
    <section className={clsx(styles.section, item.model?.fields?.mobile && styles.mobile)}>
      {item.model?.fields?.headline && (
        <SectionTitle title={item.model?.fields?.headline} link={item.model?.fields?.link as any} />
      )}
      <div className={styles['slider-section']}>
        {!['xs', 's', 'm'].includes(grid as string) && !item.model?.fields?.mobile && (
          <div className={styles.navigation}>
            <button
              ref={prevRef}
              className={clsx(
                styles['navigation-btn'],
                isPortrait ? styles['portrait-btn-position'] : styles['default-btn-position'],
              )}
            >
              <Icon name="arrowLeft" size={24} />
            </button>
            <button
              ref={nextRef}
              className={clsx(
                styles['navigation-btn'],
                isPortrait ? styles['portrait-btn-position'] : styles['default-btn-position'],
              )}
            >
              <Icon name="arrowRight" size={24} />
            </button>
          </div>
        )}
        <Swiper
          slidesPerView={
            grid === 'xs' || item.model?.fields?.mobile
              ? !isPortrait
                ? 1.2
                : 2.2
              : grid === 's'
                ? !isPortrait
                  ? 2.2
                  : 4.2
                : !isPortrait
                  ? 3
                  : 5
          }
          slidesOffsetBefore={grid === 'xs' || item.model?.fields?.mobile ? 20 : 0}
          slidesOffsetAfter={grid === 'xs' || item.model?.fields?.mobile ? 20 : 0}
          className={styles['slider-container']}
          spaceBetween={['xs', 's'].includes(grid as string) || isPortrait || item?.model?.fields.mobile ? 16 : 40}
          modules={[Navigation, Keyboard]}
          keyboard={{
            enabled: true,
            onlyInViewport: true,
          }}
          navigation={
            !['xs', 's', 'm'].includes(grid as string) &&
            !item.model?.fields?.mobile && {
              prevEl: prevRef.current,
              nextEl: nextRef.current,
            }
          }
        >
          {item.items?.map((teaser, index) => {
            return (
              <SwiperSlide key={index} className={styles.teaser}>
                <article className={styles['article-content']} title={teaser.model.headline ?? teaser.model.title}>
                  <Link
                    href={
                      teaser.model.type === 'teaserexternal' && teaser.model.linkExternal
                        ? teaser.model.linkExternal?.url
                        : teaser.model.url
                    }
                    target={
                      teaser.model.type === 'teaserexternal' && teaser.model.linkExternal
                        ? teaser.model.linkExternal?.target
                        : '_self'
                    }
                    className={styles.link}
                  >
                    <div
                      className={clsx(styles.container, !isPortrait && `aspect-${crop}`)}
                      style={{ aspectRatio: crop === 'Img_3_4' ? '61 / 85' : undefined }}
                    >
                      <Image
                        image={teaser.model.image}
                        alt={teaser.model.image?.filename || teaser.model.title}
                        className={styles.image}
                        defaultWidth={950}
                      />
                      <LiveLabel url={teaser?.model?.linkExternal?.url} position="bottom-right" />
                      <TypeVisualizer item={teaser} position="bottom-right" />
                    </div>
                    <div className={styles.headline}>{teaser.model.headline}</div>
                    {!item?.model?.fields?.hideSection && (
                      <div className={styles['section-title']}>{teaser.model.homeSection}</div>
                    )}
                  </Link>
                </article>
              </SwiperSlide>
            )
          })}
        </Swiper>
      </div>
    </section>
  )
}

export default TeaserSlider
